import { create } from "zustand";
import { deleteData, getData, postData, putData } from "../utils/formHelpers";
import {
  IFormFilterUncertaintyInputs,
  UncertaintyType,
} from "../../app/types/uncertainty.types";
import { errorAlert, successAlert } from "../../app/utils/alerts";
import { normalizeDate } from "@app/utils/functions";

export function filter(
  filters: IFormFilterUncertaintyInputs,
  uncertaintyList: UncertaintyType[]
) {
  const { date_from, date_to, type_uncertainty } = filters;

  const idsTypeUncertainty = type_uncertainty?.map((type) => type.id);

  return uncertaintyList.filter((uncertainty) => {
    const uncertaintyDateTired = new Date(uncertainty.date);
    const uncertaintyDate = normalizeDate(uncertaintyDateTired);

    if (date_from && date_to) {
      if (
        !(
          uncertaintyDate >= new Date(date_from) &&
          uncertaintyDate <= new Date(date_to)
        )
      ) {
        return false;
      }
    } else if (date_from) {
      if (!(uncertaintyDate >= new Date(date_from))) {
        return false;
      }
    } else if (date_to) {
      if (!(uncertaintyDate <= new Date(date_to))) {
        return false;
      }
    }

    if (idsTypeUncertainty && idsTypeUncertainty.length > 0) {
      const matchesTypeUncertainty = idsTypeUncertainty.includes(
        uncertainty.tipo_incertidumbre?.id
      );
      return matchesTypeUncertainty;
    }

    return true;
  });
}

type useCalibrationUncertaintyType = {
  calibrationUncertaintyList: UncertaintyType[];
  get: (id: UncertaintyType["id"]) => UncertaintyType | undefined;
  query: IFormFilterUncertaintyInputs;
  fetchAll: () => void;
  loading: boolean;
  loadingSave: boolean;
  remove: (id: UncertaintyType["id"]) => Promise<void>;
  setQuery: (query: IFormFilterUncertaintyInputs) => void;
  filteredUncertainty: () => UncertaintyType[];
  save: (uncertainty: Partial<any>) => Promise<boolean>;
};

export const useCalibrationUncertainty = create<useCalibrationUncertaintyType>(
  (set, get) => ({
    loading: false,
    loadingSave: false,
    calibrationUncertaintyList: [],
    query: {},
    setQuery: (query) => set({ query }),
    filteredUncertainty: () =>
      filter(get().query, get().calibrationUncertaintyList),
    get: (id) =>
      get().calibrationUncertaintyList.find(
        (calibration) => calibration.id === id
      ),
    fetchAll: async () => {
      set({ loading: true });
      try {
        const endpoint = "/generic/CalibrationUncertainty";
        const { data: response } = await getData(endpoint);
        set(() => ({
          calibrationUncertaintyList: response.sort(
            (a: UncertaintyType, b: UncertaintyType) =>
              (b.id as number) - (a.id as number)
          ),
          loading: false,
        }));
      } catch (error) {
        console.error("Error fetching useClient:", error);
        set({ loading: false });
      }
    },
    save: async (uncertainty: Partial<any>) => {
      let hasErrors = false;
      set({ loadingSave: true });

      try {
        const { id, ...rest } = uncertainty;
        const isNew = !id;
        const endpoint = `/generic/CalibrationUncertainty${
          !isNew ? `/${id}` : ""
        }`;

        const response = isNew
          ? await postData(endpoint, uncertainty)
          : await putData(endpoint, { ...rest });
        hasErrors = !response.success;
        if (hasErrors) {
          errorAlert("Error al guardar la incertidumbre");
        } else successAlert("Incertidumbre guardada correctamente");

        return hasErrors;
      } catch (error) {
        errorAlert("Error al guardar la incertidumbre");
        hasErrors = true;
        return hasErrors;
      } finally {
        set({ loadingSave: false });
        !hasErrors && get().fetchAll();
      }
    },

    remove: async (id: UncertaintyType["id"]) => {
      try {
        set({ loading: true });
        const endpoint = `/data/uncertainty/${id}`;

        await deleteData(endpoint);
        successAlert("Incertidumbre eliminada correctamente");
      } catch (error) {
        errorAlert("Error al eliminar la incertidumbre");
        console.error("Error deleting useCalibrationUncertainty:", error);
      } finally {
        set({ loading: false });
        get().fetchAll();
      }
    },
  })
);
